import i from 'i18next'
import { useState, Fragment } from 'react'
import { motion } from 'framer-motion'
import Portal from 'components/Portal'
import { mobileMenuMaxWidth } from 'constants/styles'
import Flyout from './Flyout'
import 'array-flat-polyfill'

function unique( value, index, self ) {
	return self.findIndex( i => i.ingredient === value.ingredient ) === index
}

export function IngredientsCTA({ product, ingredients }) {
	const [ active, setActive ] = useState( false )
	return (
		<p>
			<a className="cta" onClick={() => setActive( !active )}>
				{i.t( 'product.ingredients.cta' )}
			</a>
			<Portal selector="#__next">
				<IngredientsFlyout
					product={product}
					ingredients={ingredients}
					active={active}
					onClose={() => setActive( false )}
				/>
			</Portal>
		</p>
	)
}

type FlyoutProps = {
	product: string
	ingredients: Array<{
		ingredient: string
		purpose: string
		source: string
		products: string[]
	}>
	active: boolean
	onClose: () => void
}

// eslint-disable-next-line max-len
export default function IngredientsFlyout({ ingredients, active, onClose }: FlyoutProps ) {
	const fadeIn = {
		hidden: { opacity: 0 },
		visible: { opacity: 1 },
	}

	const products = Array
		.from( new Set( ingredients.map( i => i.products ).flat() ) )
		.map( handle => ({
			handle,
			title: handle
				.split( '-' )
				.map( w => w.charAt( 0 ).toLocaleUpperCase() + w.substr( 1 ) )
				.join( ' ' ),
			ingredients: ingredients
				.filter( ({ products }) => products.indexOf( handle ) > -1 ),
		}) )

	return (
		<Flyout active={active} onClose={onClose}>
			<article className="ingredients">
				<motion.h2 variants={fadeIn}>
					{i.t( 'product.ingredients.flyout.header' )}
				</motion.h2>

				{products.map( product => (
					<Fragment key={product.handle}>
						<motion.h3 variants={fadeIn}>{product.title}</motion.h3>
						<Hr />
						{product.ingredients
							.filter( unique )
							.map( ({ ingredient, source, purpose }) => (
								<motion.div key={ingredient} variants={fadeIn}>
									<div>
										{ingredient}
										{!!source && <span className="source">{source}</span>}
									</div>
									<div className="purpose">{purpose}</div>
								</motion.div>
							) )}
					</Fragment>
				) )}
			</article>
			<style jsx>{`
				article {
					padding-bottom: 80px;
				}

				.ingredients :global( h2 ) {
					font-size: 24px;
				}

				.ingredients :global( h3 ) {
					font-size: 15px;
					color: #8A8F95;
				}
				.ingredients {
					width: 100%;
				}
				.source {
					margin-left: .35em;
					opacity: .5;
				}
				@media ( max-width: ${mobileMenuMaxWidth}px ) {
					.source {
						opacity: 1;
					}
					.source::before {
						content: "(";
					}
					.source::after {
						content: ")";
					}
					.purpose {
						opacity: .5;
					}
				}
				.ingredients :global( hr ) {
					height: 1px;
					margin: 20px 0 35px 0;
					border-style: none;
					background-color: #E0E4E9;
				}
				@media ( max-width: ${mobileMenuMaxWidth}px ) {
					.ingredients :global( hr ) {
						margin: 20px 0;
					}
				}
				.ingredients :global( > div ) {
					white-space: pre-wrap;
					padding-bottom: 15px;
					display: flex;
				}
				.ingredients :global( > div > div:first-child ) {
					flex: 0 0 50%;
					padding-right: 1.5em;
				}
				.ingredients :global( > div > div:last-child ) {
					flex: 1;
				}
				@media ( max-width: ${mobileMenuMaxWidth}px ) {
					.ingredients :global( > div ) {
						flex-wrap: wrap;
					}
					.ingredients :global( > div > div ) {
						width: 100%;
					}
					.ingredients :global( > div > div:first-child ) {
						flex: 0 0 100%;
					}
				}
			`}</style>
		</Flyout>
	)
}

const Hr = () => (
	<motion.hr
		initial={{ scaleX: 0 }}
		variants={{
			hidden: { scaleX: 0 },
			visible: { scaleX: 1 },
		}}
		transition={{
			duration: 1,
			ease: [ 0.43, 0.13, 0.23, 0.96 ],
			delay: 0.5,
		}}
		style={{
			transformOrigin: '0% 0%',
		}}
	/>
)
